<template>
  <div
    :class="[
      'personal-plan',
      `personal-plan--${theme}`,
      { 'personal-plan--discount': isSuperDiscount },
    ]"
  >
    <h2
      v-if="title"
      :class="['h1_title', 'text-center', `title-${theme}`]"
      v-html="title"
    />
    <slot name="title" />
    <div
      v-if="timerCount > 0"
      :class="[`personal-plan__timer--${theme}`, { 'personal-plan__timer-black': isSuperDiscount }]"
      class="personal-plan__timer"
    >
      <nuxt-icon name="timer" />
      <p class="personal-plan__timer-text">
        {{ $t('lp_v7_offer_timer_message', [timer]) }}
      </p>
    </div>

    <div
      v-if="isSuperDiscount && $versions?.landing !== '15.2'"
      :class="[theme, { 'personal-plan__gift--hide-old-discount': shouldHideOldDiscount }]"
      class="personal-plan__gift"
    >
      <CNuxtImg
        alt="gift"
        class="personal-plan__gift-image"
        min-height="49px"
        src="/images/gift-red.webp"
      />
      <span
        class="paragraph-16-600 personal-plan__gift-text"
        v-html="$t('lp_v7_discount_offer_message', [
          standardDiscountPercent,
          superDiscountPercent,
        ])"
      />
    </div>

    <div class="personal-plan__items-wrapper">
      <slot />
    </div>

    <p
      v-if="!showFooter && !hideDisclaimer"
      class="personal-plan__disclaimer small_text text-center"
      v-html="disclaimer"
    />

    <base-button
      v-if="!showFooter"
      :type="buttonType"
      class="personal-plan__button"
      data-testid="checkout-open-btnCheckout"
      with-shadow
      @click="emits('getPlan')"
    >
      {{ $t('lp_v7_get_plan_button2') }}
    </base-button>

    <div
      v-if="!showFooter"
      class="personal-plan__guarantee"
    >
      <nuxt-icon
        :class="theme"
        filled
        name="star-check"
      />
      <p class="personal-plan__guarantee-text paragraph-14-600 text-center">
        {{ $t('lp_v7_money_back_note') }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { calculateDiscountPercent, resolveLandingSubscriptionDisclaimer } from '../../../../core/payments/shared/utils/pricing';

const props = defineProps({
  title: String,
  isSuperDiscount: {
    type: Boolean,
    default: false,
  },
  timer: {
    type: String,
    required: true,
  },
  theme: {
    type: String,
  },
  timerCount: {
    type: Number,
    required: true,
  },
  selectedPlan: {
    type: Object,
    required: true,
  },
  hideDisclaimer: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(['getPlan']);
const { t } = useI18n();
const { $versions } = useNuxtApp();

const buttonType = computed(() => {
  switch (props.theme) {
    case 'green':
      return 'green';
    case 'orange':
      return 'red';
    default:
      return props.isSuperDiscount ? 'red' : 'blue';
  }
});

const disclaimer = computed(() => {
  return resolveLandingSubscriptionDisclaimer(
    props.selectedPlan as SubscriptionModel,
    t,
    props.isSuperDiscount,
  );
});

const standardDiscountPercent = computed(() => {
  return calculateDiscountPercent(props.selectedPlan as SubscriptionModel, props.selectedPlan.standard);
});

const superDiscountPercent = computed(() => {
  return calculateDiscountPercent(props.selectedPlan as SubscriptionModel, props.selectedPlan.super, { useRenewalPrice: true });
});

const shouldHideOldDiscount = computed(() => {
  return standardDiscountPercent.value - superDiscountPercent.value === 0;
});

const showFooter = computed(() => {
  return $versions?.value.landing === '14' || $versions?.value.landing === '14.1' || $versions?.value.landing === '15.2';
});
</script>

<style lang="scss" scoped>
  .personal-plan {
    display: flex;
    flex-direction: column;
    background: $bg-shade-blue;
    padding: 32px;
    padding-bottom: 48px;
    width: 100%;

    &__timer {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: 42px;
      width: 100%;
      background: $red;
      border-radius: 9px;
      color: $white;
      font-size: 16px;
      margin-top: 32px;

      .nuxt-icon {
        margin-bottom: 4px;
      }
    }

    &__timer-black {
      background: $text-color;
    }

    &__timer-text {
      font-family: $primary-fonts;
      font-size: 13px;
      font-style: normal;
      font-weight: 700;
      line-height: 17px;
    }

    &__items-wrapper {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 16px;
      margin-top: 26px;
    }

    &__guarantee {
      display: flex;
      justify-content: center;
      width: 100%;
      gap: 8px;
      margin-top: 32px;

      .nuxt-icon {
        line-height: 0;
        font-size: 19px;
      }
    }

    &__guarantee-text {
      margin-left: 8px;
      font-family: $primary-fonts;
      font-weight: 700 !important;
      color: #6F6F6F;
    }

    &__button {
      margin-top: 16px;
      border-radius: 100px;
    }

    &__disclaimer {
      margin-top: 32px;
      font-size: 12px;
      opacity: 1;
      color: #6F6F6F;
    }

    &__gift {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 20px;
      border-radius: 9px;
      border: 2px solid var(--theme-color);
      background: $bg-shade-red;
      margin-top: 24px;
    }

    &__gift-image {
      width: 64px;
    }

    &__gift-text {
      font-weight: 700;
    }

  }
</style>

<style lang="scss">
  .old-discount {
    text-decoration: line-through;
    color: rgba(17, 17, 19, 0.50);
  }

  .personal-plan__gift--hide-old-discount .old-discount {
    display: none;
  }

  .super-discount {
    color: $red;
  }
</style>
